/**
 * API エラーコード → ユーザーフレンドリーな日本語メッセージの変換。
 *
 * admin-api が返すエラーコード (例: "invalid_credentials") を
 * ユーザーに分かりやすい日本語に変換する。
 */

import { ApiError } from './api-client.js';

const ERROR_MESSAGES: Record<string, string> = {
  // 認証
  invalid_credentials: 'メールアドレスまたはパスワードが正しくありません',
  account_locked: 'アカウントがロックされています。しばらく経ってから再度お試しください',
  unauthorized: 'ログインが必要です',
  forbidden: 'この操作を実行する権限がありません',
  sudo_required: 'セキュリティのため、パスワードを再入力してください',
  weak_password: 'パスワードが弱すぎます。12文字以上で英数字記号を組み合わせてください',
  token_expired: '有効期限が切れました。再度お試しください',
  token_consumed: 'このリンクは既に使用されています',
  token_revoked: 'このトークンは無効化されています',
  token_reuse_detected:
    'セキュリティのため、すべてのセッションを無効化しました。再度ログインしてください',
  invalid_token: '無効なリンクです',
  invalid_signature: '署名が正しくありません',
  missing_signature: '署名が必要です',

  // 招待
  invitation_not_found: '招待が見つかりません',
  invitation_expired: '招待の有効期限が切れています',
  invitation_cancelled: 'この招待はキャンセルされました',
  invitation_declined: 'この招待は辞退されました',
  invitation_already_accepted: 'この招待は既に承認されています',
  invite_email_mismatch: '招待されたメールアドレスと異なります',

  // リソース
  not_found: 'リソースが見つかりません',
  site_not_found: 'サイトが見つかりません',
  user_not_found: 'ユーザーが見つかりません',
  member_not_found: 'メンバーが見つかりません',
  member_exists: 'このユーザーは既にメンバーです',
  owner_delete_forbidden: 'オーナーは削除できません',
  last_owner_guard: '最後のオーナーは削除できません',

  // バリデーション
  bad_request: 'リクエストの形式が正しくありません',
  invalid_payload: '入力内容に誤りがあります',
  invalid_query: 'クエリの形式が正しくありません',
  invalid_hostname: 'ホスト名の形式が正しくありません',
  invalid_mime: '対応していないファイル形式です (JPEG/PNG/GIF/WebP/AVIF/SVG のみ)',
  invalid_key: 'キーの形式が正しくありません',
  validation_error: '入力内容に誤りがあります',
  file_too_large: 'ファイルサイズが大きすぎます (最大 10MB)',
  missing_site_id: 'サイトIDが指定されていません',

  // 競合・重複
  duplicate: '既に同じ項目が存在します',
  slug_conflict: 'このスラッグは既に使われています。別のスラッグを指定してください',
  snippet_conflict: 'このキーは既に使われています',
  hostname_conflict: 'このホスト名は既に他のサイトで使用されています',
  rev_conflict: '他のユーザーが編集しました。最新内容を読み込んで再度保存してください',
  expected_rev_required: 'リビジョンが指定されていません',

  // 課金 / プラン上限
  no_subscription: 'サブスクリプションがありません',
  stripe_error: '決済処理でエラーが発生しました。時間を置いて再度お試しください',
  PLAN_LIMIT_EXCEEDED: 'プランの上限に達しました。プランをアップグレードしてください',
  PLAN_FEATURE_LOCKED: 'この機能は現在のプランではご利用いただけません',

  // サーバー
  cf_api_error: 'カスタムドメインの設定に失敗しました',
  cf_not_configured: 'カスタムドメイン機能が利用できません',
  auth_not_configured: '認証設定が未完了です',
  registration_failed: '登録に失敗しました',
  schema_not_migrated: 'データベースの準備が完了していません',
  no_changes: '変更点がありません',
  unknown: '不明なエラーが発生しました',

  // HTTP status fallback
  http_400: '入力内容に誤りがあります',
  http_401: 'ログインが必要です',
  http_403: 'この操作を実行する権限がありません',
  http_404: 'リソースが見つかりません',
  http_409: '競合が発生しました。最新状態を確認してください',
  http_413: 'リクエストが大きすぎます',
  http_429: 'リクエストが多すぎます。しばらく経ってから再度お試しください',
  http_500: 'サーバーエラーが発生しました',
  http_502: 'サーバーに接続できません',
  http_503: 'サービスが一時的に利用できません',
};

/**
 * エラーをユーザーフレンドリーなメッセージに変換する。
 *
 * - ApiError: コードから日本語メッセージを解決
 * - それ以外: 元のメッセージ or 'エラーが発生しました'
 */
/** 409 Conflict (楽観的ロック競合) 判定ヘルパー */
export function isRevConflict(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

export function formatErrorMessage(error: unknown, fallback = 'エラーが発生しました'): string {
  if (error instanceof ApiError) {
    const mapped = ERROR_MESSAGES[error.code];
    if (mapped) return mapped;
    // code not mapped → fallback to server message if Japanese-looking
    if (error.message && /[ぁ-んァ-ヶ一-龯]/.test(error.message)) return error.message;
    return fallback;
  }
  if (error instanceof Error && /[ぁ-んァ-ヶ一-龯]/.test(error.message)) {
    return error.message;
  }
  return fallback;
}
